import axios from "axios";
import { api } from "./api";
import type {
  AdminModel,
  ModelPatch,
  ModelProvider,
  ModelProviderWrite,
  ModelRoleBinding,
  ModelRoleName,
  ModelTestResult,
  ModelWrite,
  VendorCatalogEntry,
  VendorEndpoint,
  VendorSnapshot,
} from "@/types/modelRegistry";

const BASE = "/admin/model-registry";

/** The server answers refusals with `{ error }`; surface that line, else the transport message. */
export const registryErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: string } | undefined;
    return body?.error ?? error.message;
  }
  return error instanceof Error ? error.message : "Request failed";
};

export const modelRegistryApi = {
  listProviders: async (): Promise<ModelProvider[]> =>
    (await api.get<{ providers: ModelProvider[] }>(`${BASE}/providers`)).data
      .providers,

  upsertProvider: async (body: ModelProviderWrite): Promise<ModelProvider> =>
    (await api.put<ModelProvider>(`${BASE}/providers`, body)).data,

  listModels: async (): Promise<AdminModel[]> =>
    (await api.get<{ models: AdminModel[] }>(`${BASE}/models`)).data.models,

  createModel: async (body: ModelWrite): Promise<AdminModel> =>
    (await api.post<AdminModel>(`${BASE}/models`, body)).data,

  updateModel: async (id: string, patch: ModelPatch): Promise<AdminModel> =>
    (await api.patch<AdminModel>(`${BASE}/models/${id}`, patch)).data,

  deleteModel: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/models/${id}`);
  },

  syncModel: async (id: string): Promise<AdminModel> =>
    (await api.post<AdminModel>(`${BASE}/models/${id}/sync`, {})).data,

  testModel: async (id: string): Promise<ModelTestResult> =>
    (
      await api.post<ModelTestResult>(
        `${BASE}/models/${id}/test`,
        {},
        { timeout: 45_000 },
      )
    ).data,

  listRoles: async (): Promise<ModelRoleBinding[]> =>
    (await api.get<{ roles: ModelRoleBinding[] }>(`${BASE}/roles`)).data.roles,

  setRole: async (
    role: ModelRoleName,
    modelId: string,
  ): Promise<ModelRoleBinding> =>
    (await api.put<ModelRoleBinding>(`${BASE}/roles/${role}`, { modelId }))
      .data,

  vendorCatalog: async (): Promise<VendorCatalogEntry[]> =>
    (
      await api.get<{ entries: VendorCatalogEntry[] }>(
        `${BASE}/vendor/catalog`,
        { timeout: 30_000 },
      )
    ).data.entries,

  vendorEndpoints: async (
    modelName: string,
  ): Promise<VendorEndpoint[] | null> => {
    try {
      return (
        await api.get<{ endpoints: VendorEndpoint[] }>(
          `${BASE}/vendor/endpoints`,
          {
            params: { modelName },
            timeout: 30_000,
          },
        )
      ).data.endpoints;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404)
        return null;
      throw error;
    }
  },

  vendorLookup: async (modelName: string): Promise<VendorSnapshot | null> => {
    try {
      return (
        await api.get<VendorSnapshot>(`${BASE}/vendor/lookup`, {
          params: { modelName },
        })
      ).data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404)
        return null;
      throw error;
    }
  },
};
